import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCheck,
  FiEdit,
  FiPlus,
  FiShield,
  FiUsers,
  FiX,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const emptyForm = {
  code: "",
  name: "",
  description: "",
  permissionCodes: [],
};

export default function RolesPermissions() {
  const { accessToken } = useAuth();

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError("");

    try {
      const [rolesResponse, permissionsResponse] = await Promise.all([
        apiRequest("/roles", { accessToken }),
        apiRequest("/roles/permissions", { accessToken }),
      ]);

      const roleList = rolesResponse || [];

      setRoles(roleList);
      setPermissions(permissionsResponse || []);

      setSelectedRoleId((currentRoleId) => {
        const exists = roleList.some((role) => role.id === currentRoleId);

        return exists ? currentRoleId : roleList[0]?.id || "";
      });
    } catch (requestError) {
      setError(requestError.message || "Unable to load roles and permissions.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedRole = roles.find((role) => role.id === selectedRoleId);

  const selectedRolePermissions = useMemo(
    () =>
      new Set(
        selectedRole?.permissions?.map(
          (rolePermission) => rolePermission.permission?.code,
        ) || [],
      ),
    [selectedRole],
  );

  const permissionsByModule = useMemo(() => {
    return permissions.reduce((groups, permission) => {
      const moduleName = permission.module || "other";

      if (!groups[moduleName]) {
        groups[moduleName] = [];
      }

      groups[moduleName].push(permission);
      return groups;
    }, {});
  }, [permissions]);

  const stats = useMemo(() => {
    return {
      totalRoles: roles.length,
      systemUsers: roles.reduce(
        (sum, role) => sum + (role._count?.users || 0),
        0,
      ),
      systemRoles: roles.filter((role) => role.isSystem).length,
      customRoles: roles.filter((role) => !role.isSystem).length,
    };
  }, [roles]);

  const openCreateModal = () => {
    setEditingRole(null);
    setModalOpen(true);
  };

  const openEditModal = (role) => {
    if (role.isSystem) {
      setError("System roles cannot be changed.");
      return;
    }

    setEditingRole(role);
    setModalOpen(true);
  };

  const saveRole = async (form) => {
    setSaving(true);
    setError("");

    try {
      if (editingRole) {
        await apiRequest(`/roles/${editingRole.id}`, {
          method: "PATCH",
          accessToken,
          body: {
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            permissionCodes: form.permissionCodes,
          },
        });
      } else {
        await apiRequest("/roles", {
          method: "POST",
          accessToken,
          body: {
            code: form.code.trim().toUpperCase(),
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            permissionCodes: form.permissionCodes,
          },
        });
      }

      setModalOpen(false);
      setEditingRole(null);
      await loadData();
    } catch (requestError) {
      setError(requestError.message || "Unable to save role.");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-80 flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-primary-100 border-t-primary-800 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Roles & Permissions
          </h1>

          <p className="mt-2 text-gray-500">
            Control user access and system permissions.
          </p>
        </div>

        <button
          onClick={openCreateModal}
          className="px-5 py-3 rounded-2xl bg-primary-800 text-white flex items-center gap-2 hover:bg-primary-900 transition"
        >
          <FiPlus />
          Add Role
        </button>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard
          title="Total Roles"
          value={stats.totalRoles}
          icon={<FiShield size={22} />}
        />

        <StatCard
          title="Assigned Users"
          value={stats.systemUsers}
          icon={<FiUsers size={22} />}
        />

        <StatCard
          title="System Roles"
          value={stats.systemRoles}
          icon={<FiShield size={22} />}
        />

        <StatCard
          title="Custom Roles"
          value={stats.customRoles}
          icon={<FiCheck size={22} />}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        {roles.map((role) => {
          const selected = role.id === selectedRoleId;

          return (
            <button
              key={role.id}
              onClick={() => setSelectedRoleId(role.id)}
              className={`text-left bg-white rounded-3xl border p-6 shadow-sm hover:shadow-lg transition-all ${
                selected
                  ? "border-primary-500 ring-2 ring-primary-100"
                  : "border-gray-100"
              }`}
            >
              <div className="h-14 w-14 rounded-2xl bg-primary-50 text-primary-700 flex items-center justify-center">
                <FiShield size={24} />
              </div>

              <div className="mt-5 flex items-start justify-between gap-2">
                <h3 className="font-semibold text-gray-900">{role.name}</h3>

                {role.isSystem ? (
                  <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-semibold text-gray-600">
                    SYSTEM
                  </span>
                ) : null}
              </div>

              <p className="mt-2 text-sm text-gray-500 line-clamp-2">
                {role.description || "No description added."}
              </p>

              <div className="mt-5 flex items-center justify-between">
                <span className="text-sm text-gray-500">Assigned Users</span>

                <span className="font-semibold text-primary-700">
                  {role._count?.users || 0}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Permission Matrix
            </h3>

            <p className="mt-1 text-sm text-gray-500">
              {selectedRole
                ? `Permissions granted to ${selectedRole.name}`
                : "Select a role to view permissions."}
            </p>
          </div>

          {selectedRole ? (
            <span className="rounded-full bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-800">
              {selectedRolePermissions.size} permission(s)
            </span>
          ) : null}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Module
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Available Permissions
                </th>

                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Granted
                </th>
              </tr>
            </thead>

            <tbody>
              {Object.entries(permissionsByModule).map(
                ([moduleName, modulePermissions]) => {
                  const granted = modulePermissions.filter((permission) =>
                    selectedRolePermissions.has(permission.code),
                  );

                  return (
                    <tr
                      key={moduleName}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-6 py-5 font-semibold text-gray-900 capitalize">
                        {moduleName.replaceAll("_", " ")}
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex flex-wrap gap-2">
                          {modulePermissions.map((permission) => (
                            <span
                              key={permission.code}
                              className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs text-gray-600"
                            >
                              {permission.action}
                            </span>
                          ))}
                        </div>
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex flex-wrap gap-2">
                          {granted.length ? (
                            granted.map((permission) => (
                              <span
                                key={permission.code}
                                className="inline-flex items-center gap-1 rounded-lg bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700"
                              >
                                <FiCheck size={13} />
                                {permission.action}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-400">
                              No permissions granted
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                },
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">System Roles</h3>
        </div>

        <div className="divide-y divide-gray-100">
          {roles.map((role) => (
            <div
              key={role.id}
              className="p-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
            >
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-900">{role.name}</h4>

                  <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
                    {role.code}
                  </span>

                  {role.isSystem ? (
                    <span className="rounded-md bg-primary-50 px-2 py-1 text-xs font-semibold text-primary-800">
                      System
                    </span>
                  ) : null}
                </div>

                <p className="mt-1 text-sm text-gray-500">
                  {role.description || "No description added."}
                </p>
              </div>

              <button
                disabled={role.isSystem}
                onClick={() => openEditModal(role)}
                className="px-4 py-2 rounded-xl bg-primary-50 text-primary-700 hover:bg-primary-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {role.isSystem ? "Protected" : "Edit"}
              </button>
            </div>
          ))}
        </div>
      </div>

      <RoleModal
        open={modalOpen}
        initialData={editingRole}
        permissionsByModule={permissionsByModule}
        saving={saving}
        onClose={() => {
          if (!saving) {
            setModalOpen(false);
            setEditingRole(null);
          }
        }}
        onSave={saveRole}
      />
    </div>
  );
}

function RoleModal({
  open,
  initialData,
  permissionsByModule,
  saving,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    setForm(
      initialData
        ? {
            code: initialData.code || "",
            name: initialData.name || "",
            description: initialData.description || "",
            permissionCodes:
              initialData.permissions
                ?.map((item) => item.permission?.code)
                .filter(Boolean) || [],
          }
        : emptyForm,
    );
  }, [open, initialData]);

  if (!open) return null;

  const allPermissionCodes = Object.values(permissionsByModule)
    .flat()
    .map((permission) => permission.code);

  const selectedPermissionCodes = new Set(form.permissionCodes);

  const allPermissionsSelected =
    allPermissionCodes.length > 0 &&
    allPermissionCodes.every((code) => selectedPermissionCodes.has(code));

  const togglePermission = (permissionCode) => {
    setForm((previous) => ({
      ...previous,
      permissionCodes: previous.permissionCodes.includes(permissionCode)
        ? previous.permissionCodes.filter((code) => code !== permissionCode)
        : [...previous.permissionCodes, permissionCode],
    }));
  };

  const toggleAllPermissions = () => {
    setForm((previous) => ({
      ...previous,
      permissionCodes: allPermissionsSelected ? [] : allPermissionCodes,
    }));
  };

  const toggleModulePermissions = (modulePermissions) => {
    const moduleCodes = modulePermissions.map((permission) => permission.code);

    setForm((previous) => {
      const selectedCodes = new Set(previous.permissionCodes);
      const moduleIsFullySelected = moduleCodes.every((code) =>
        selectedCodes.has(code),
      );

      if (moduleIsFullySelected) {
        moduleCodes.forEach((code) => selectedCodes.delete(code));
      } else {
        moduleCodes.forEach((code) => selectedCodes.add(code));
      }

      return {
        ...previous,
        permissionCodes: Array.from(selectedCodes),
      };
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm">
      <div className="flex h-[90dvh] w-full flex-col bg-gray-50">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-white px-5 py-4 sm:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary-700">
              Roles & Permissions
            </p>

            <h3 className="mt-1 text-xl font-bold text-gray-900 sm:text-2xl">
              {initialData ? "Edit Role" : "Create New Role"}
            </h3>

            <p className="mt-1 text-sm text-gray-500">
              Configure access section by section, or grant every permission.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Close role editor"
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-100 text-gray-700 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiX size={22} />
          </button>
        </div>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            onSave(form);
          }}
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto w-full space-y-7 p-5 sm:p-8">
              <section className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-5">
                  <h4 className="text-lg font-bold text-gray-900">
                    Role details
                  </h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Give this role a clear name and code.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Role Name *
                    </label>

                    <input
                      required
                      value={form.name}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          name: event.target.value,
                        }))
                      }
                      placeholder="Store Manager"
                      className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Role Code *
                    </label>

                    <input
                      required
                      disabled={Boolean(initialData)}
                      value={form.code}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          code: event.target.value.toUpperCase(),
                        }))
                      }
                      placeholder="STORE_MANAGER"
                      className="w-full rounded-2xl border border-gray-200 px-4 py-3 uppercase outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Description
                    </label>

                    <textarea
                      rows="3"
                      value={form.description}
                      onChange={(event) =>
                        setForm((previous) => ({
                          ...previous,
                          description: event.target.value,
                        }))
                      }
                      placeholder="Describe this role and its responsibilities."
                      className="w-full resize-none rounded-2xl border border-gray-200 px-4 py-3 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>
              </section>

              <section className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm">
                <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">
                      Permissions
                    </h4>
                    <p className="mt-1 text-sm text-gray-500">
                      {form.permissionCodes.length} of{" "}
                      {allPermissionCodes.length} permissions selected
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={toggleAllPermissions}
                    disabled={!allPermissionCodes.length}
                    className={`inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                      allPermissionsSelected
                        ? "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                        : "bg-primary-800 text-white hover:bg-primary-900"
                    }`}
                  >
                    <FiCheck size={17} />
                    {allPermissionsSelected
                      ? "Clear All Permissions"
                      : "Select All Permissions"}
                  </button>
                </div>

                <div className="space-y-5 bg-gray-50 p-5 sm:p-6">
                  {Object.entries(permissionsByModule).map(
                    ([moduleName, modulePermissions]) => {
                      const moduleCodes = modulePermissions.map(
                        (permission) => permission.code,
                      );

                      const selectedInModule = moduleCodes.filter((code) =>
                        selectedPermissionCodes.has(code),
                      ).length;

                      const moduleFullySelected =
                        moduleCodes.length > 0 &&
                        selectedInModule === moduleCodes.length;

                      return (
                        <section
                          key={moduleName}
                          className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
                        >
                          <div className="flex flex-col gap-3 border-b border-gray-100 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                            <div>
                              <h5 className="font-bold capitalize text-gray-900">
                                {moduleName.replaceAll("_", " ")}
                              </h5>

                              <p className="mt-1 text-xs text-gray-500">
                                {selectedInModule} of {moduleCodes.length}{" "}
                                selected
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                toggleModulePermissions(modulePermissions)
                              }
                              className={`rounded-xl px-3 py-2 text-xs font-bold transition ${
                                moduleFullySelected
                                  ? "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                                  : "border border-primary-200 bg-primary-50 text-primary-800 hover:bg-primary-100"
                              }`}
                            >
                              {moduleFullySelected
                                ? `Clear ${moduleName.replaceAll("_", " ")}`
                                : `Select All ${moduleName.replaceAll(
                                    "_",
                                    " ",
                                  )}`}
                            </button>
                          </div>

                          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3 sm:p-5">
                            {modulePermissions.map((permission) => {
                              const checked = selectedPermissionCodes.has(
                                permission.code,
                              );

                              return (
                                <label
                                  key={permission.code}
                                  className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-3.5 transition ${
                                    checked
                                      ? "border-primary-300 bg-primary-50"
                                      : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() =>
                                      togglePermission(permission.code)
                                    }
                                    className="mt-0.5 h-4 w-4 accent-primary-800"
                                  />

                                  <span className="min-w-0">
                                    <span className="block text-sm font-semibold capitalize text-gray-800">
                                      {permission.action.replaceAll("_", " ")}
                                    </span>

                                    <span className="mt-1 block break-all text-xs text-gray-500">
                                      {permission.code}
                                    </span>
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </section>
                      );
                    },
                  )}
                </div>
              </section>
            </div>
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-gray-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <p className="text-sm text-gray-500">
              {form.permissionCodes.length} permission(s) will be granted.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                disabled={saving}
                onClick={onClose}
                className="rounded-2xl border border-gray-200 px-5 py-3 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving || !form.permissionCodes.length}
                className="rounded-2xl bg-primary-800 px-5 py-3 font-semibold text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving
                  ? "Saving..."
                  : initialData
                    ? "Save Role"
                    : "Create Role"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="mt-3 text-3xl font-bold text-gray-900">{value}</h3>
        </div>

        <div className="h-14 w-14 rounded-2xl bg-primary-50 text-primary-700 flex items-center justify-center">
          {icon}
        </div>
      </div>
    </div>
  );
}
